import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { StarInput } from "@/components/Reviews";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export function ReviewDialog({ booking, who, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/review`, { rating, comment });
      toast.success("Review posted");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`review-open-${booking.booking_id}`}>Review {who}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review {who}</DialogTitle>
          <DialogDescription>Your review appears publicly so others can decide with confidence.</DialogDescription>
        </DialogHeader>
        <StarInput value={rating} onChange={setRating} />
        <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was it?" rows={4} />
        <Button data-testid="review-submit-button" disabled={busy || comment.trim().length < 3} onClick={submit} className="rounded-full">Post review</Button>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORIES = ["Cleanliness", "Not as described", "Safety issue", "Amenities missing", "Access / check-in problem", "Other"];

export function ComplaintDialog({ booking }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/complaints", { booking_id: booking.booking_id, category, description, evidence });
      toast.success("Complaint filed. The host has been notified.");
      navigate(`/complaints/${data.complaint_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="rounded-full text-secondary hover:text-secondary" data-testid={`complaint-open-${booking.booking_id}`}>Report a problem</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report a problem with this stay</DialogTitle>
          <DialogDescription>Add clear photos and details. Our team reviews both sides and decides based on the evidence.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>What went wrong?</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="complaint-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Describe it</Label>
          <Textarea data-testid="complaint-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="What happened, when, and what you'd like done" />
        </div>
        <div className="space-y-2">
          <Label>Photo evidence</Label>
          <PhotoUploader value={evidence} onChange={setEvidence} kind="evidence" max={10} testid="complaint-evidence" />
        </div>
        <Button data-testid="complaint-submit-button" disabled={busy || !category || description.trim().length < 10} onClick={submit} className="rounded-full">Submit complaint</Button>
      </DialogContent>
    </Dialog>
  );
}
